import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ShieldAlert,
  Activity,
  Ban,
  Gauge,
  Trash2,
  RefreshCw,
  Search,
  UserCheck,
} from "lucide-react";
import {
  adminClearAuditLog,
  adminGetRateLimits,
  adminListAuditLog,
  adminListBannedUsers,
  adminSaveRateLimits,
  adminSecurityOverview,
  adminUnbanUser,
} from "@/lib/admin-security.functions";
import { SecretRotationTab } from "@/components/admin/secret-rotation-tab";

export const Route = createFileRoute("/_authenticated/admin/security")({
  component: SecurityTab,
});

type Plan = "free" | "pro" | "elite";

function SecurityTab() {
  const qc = useQueryClient();
  const overviewFn = useServerFn(adminSecurityOverview);
  const auditFn = useServerFn(adminListAuditLog);
  const bannedFn = useServerFn(adminListBannedUsers);
  const unbanFn = useServerFn(adminUnbanUser);
  const limitsFn = useServerFn(adminGetRateLimits);
  const saveLimitsFn = useServerFn(adminSaveRateLimits);
  const clearFn = useServerFn(adminClearAuditLog);

  const [actionFilter, setActionFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");

  const overview = useQuery({
    queryKey: ["admin", "security", "overview"],
    queryFn: () => overviewFn(),
  });

  const audit = useQuery({
    queryKey: ["admin", "security", "audit", actionFilter, emailFilter],
    queryFn: () =>
      auditFn({
        data: {
          limit: 100,
          action: actionFilter || undefined,
          actorEmail: emailFilter || undefined,
        },
      }),
  });

  const banned = useQuery({
    queryKey: ["admin", "security", "banned"],
    queryFn: () => bannedFn(),
  });

  const limits = useQuery({
    queryKey: ["admin", "security", "limits"],
    queryFn: () => limitsFn(),
  });

  const unbanMut = useMutation({
    mutationFn: (userId: string) => unbanFn({ data: { userId } }),
    onSuccess: () => {
      toast.success("User unbanned");
      qc.invalidateQueries({ queryKey: ["admin", "security"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const clearMut = useMutation({
    mutationFn: (days: number) => clearFn({ data: { olderThanDays: days } }),
    onSuccess: (r) => {
      toast.success(`Deleted ${r.deleted} entries`);
      qc.invalidateQueries({ queryKey: ["admin", "security"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2.5">
        <div className="rounded-md border border-primary/40 bg-primary/10 p-1.5 text-primary">
          <ShieldAlert className="h-4 w-4" />
        </div>
        <h2 className="text-lg font-bold tracking-tight">Security & Audit</h2>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Total events" value={overview.data?.totalEvents ?? 0} icon={Activity} />
        <StatCard label="Last 7 days" value={overview.data?.recentEvents ?? 0} icon={Activity} />
        <StatCard label="Banned users" value={overview.data?.bannedCount ?? 0} icon={Ban} />
        <StatCard label="Admins" value={overview.data?.adminCount ?? 0} icon={ShieldAlert} />
      </div>

      <Tabs defaultValue="audit" className="space-y-4">
        <TabsList>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
          <TabsTrigger value="banned">Banned Users</TabsTrigger>
          <TabsTrigger value="limits">Rate Limits</TabsTrigger>
          <TabsTrigger value="rotation">🔐 Secret Rotation</TabsTrigger>
        </TabsList>

        <TabsContent value="rotation">
          <SecretRotationTab />
        </TabsContent>

        <TabsContent value="audit" className="space-y-3">
          <Card className="border-border/60 bg-card/40 p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs">Action filter</Label>
                <Input
                  placeholder="e.g. user.unban"
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                />
              </div>
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs">Actor email</Label>
                <Input
                  placeholder="search by email"
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => audit.refetch()}
                disabled={audit.isFetching}
              >
                <Search className="mr-2 h-3.5 w-3.5" />
                Search
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (confirm("Delete audit entries older than 90 days?")) clearMut.mutate(90);
                }}
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Purge {">"}90d
              </Button>
            </div>
          </Card>

          <Card className="border-border/60 bg-card/40">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Metadata</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audit.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : (audit.data?.entries ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No audit entries.
                    </TableCell>
                  </TableRow>
                ) : (
                  audit.data!.entries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {new Date(e.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs">
                        {e.actor_email ?? (
                          <span className="text-muted-foreground">system</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-[10px]">
                          {e.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {e.target_type ? (
                          <span>
                            <span className="text-muted-foreground">{e.target_type}:</span>{" "}
                            <span className="font-mono">{e.target_id?.slice(0, 12)}</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[280px] truncate font-mono text-[10px] text-muted-foreground">
                        {e.metadata ? JSON.stringify(e.metadata) : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="banned" className="space-y-3">
          <Card className="border-border/60 bg-card/40">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(banned.data?.users ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No banned users.
                    </TableCell>
                  </TableRow>
                ) : (
                  banned.data!.users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>
                        <div className="text-sm font-medium">{u.display_name ?? u.email}</div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{u.plan}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {u.last_seen_at ? new Date(u.last_seen_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => unbanMut.mutate(u.id)}
                          disabled={unbanMut.isPending}
                        >
                          <UserCheck className="mr-2 h-3.5 w-3.5" />
                          Unban
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="limits" className="space-y-3">
          {limits.data && (
            <RateLimitsEditor
              initial={limits.data.limits}
              onSave={async (v) => {
                await saveLimitsFn({ data: v });
                toast.success("Rate limits saved");
                qc.invalidateQueries({ queryKey: ["admin", "security", "limits"] });
              }}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card className="border-border/60 bg-card/40 p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="mt-1 text-2xl font-bold">{value}</div>
        </div>
        <Icon className="h-5 w-5 text-primary" />
      </div>
    </Card>
  );
}

type Limits = {
  free: { signals_per_day: number; backtests_per_day: number };
  pro: { signals_per_day: number; backtests_per_day: number };
  elite: { signals_per_day: number; backtests_per_day: number };
};

function RateLimitsEditor({
  initial,
  onSave,
}: {
  initial: Limits;
  onSave: (v: Limits) => Promise<void>;
}) {
  const [state, setState] = useState<Limits>(initial);
  const [saving, setSaving] = useState(false);

  const update = (plan: Plan, key: "signals_per_day" | "backtests_per_day", value: number) =>
    setState((s) => ({ ...s, [plan]: { ...s[plan], [key]: value } }));

  return (
    <Card className="border-border/60 bg-card/40 p-5">
      <div className="mb-4 flex items-center gap-2">
        <Gauge className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-bold">Per-plan daily limits</h3>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {(["free", "pro", "elite"] as const).map((plan) => (
          <div key={plan} className="rounded-md border border-border/60 bg-background/40 p-4">
            <div className="mb-3 flex items-center justify-between">
              <Badge variant="outline" className="uppercase">
                {plan}
              </Badge>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Signals / day</Label>
                <Input
                  type="number"
                  min={0}
                  value={state[plan].signals_per_day}
                  onChange={(e) => update(plan, "signals_per_day", Number(e.target.value))}
                />
              </div>
              <div>
                <Label className="text-xs">Backtests / day</Label>
                <Input
                  type="number"
                  min={0}
                  value={state[plan].backtests_per_day}
                  onChange={(e) => update(plan, "backtests_per_day", Number(e.target.value))}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setState(initial)}
          disabled={saving}
        >
          <RefreshCw className="mr-2 h-3.5 w-3.5" />
          Reset
        </Button>
        <Button
          size="sm"
          onClick={async () => {
            setSaving(true);
            try {
              await onSave(state);
            } finally {
              setSaving(false);
            }
          }}
          disabled={saving}
        >
          Save limits
        </Button>
      </div>
    </Card>
  );
}
